import { manifestLoader } from './manifest-loader';
import { scheduler, type ActivePlaylist, type PowerScheduleStatus, type ScheduleStatus } from './scheduler';
import { renderer } from './renderer';
import { layoutManager } from './layout-manager';
import { overlayRenderer, type OverlayConfig } from './overlay-renderer';
import { videoWallManager } from './video-wall-manager';
import { syncManager } from './sync-manager';
import { analytics } from './analytics';
import { eventReporter } from './event-reporter';
import { fallbackManager, type FallbackState } from './fallback-manager';
import { ClockWidget } from './widgets/clock-widget';
import { WeatherWidget } from './widgets/weather-widget';
import { QRCodeWidget } from './widgets/qr-widget';
import { RSSWidget } from './widgets/rss-widget';
import type { Widget } from './widgets';
import { captureAndUploadScreenshot } from './screenshot-capture';
import { api } from '../utils/api';
import { getDeviceRegistration, setDeviceRegistration, clearAllData } from '../utils/storage';
import type { DeviceManifest, DeviceRegistration, PlaylistItem } from '../types';
import { config } from '../config';

export type PlayerStatus = 'loading' | 'playing' | 'error' | 'offline' | 'paused' | 'no_content';

export interface PlayerStatusUpdate {
  status: PlayerStatus;
  message?: string;
  playlist?: string;
  item?: PlaylistItem;
}

export type StatusCallback = (update: PlayerStatusUpdate) => void;

class Player {
  private registration: DeviceRegistration | null = null;
  private currentPlaylist: ActivePlaylist | null = null;
  private currentItemIndex = 0;
  // Wraps the raw currentItemIndex write with a trace log so every transition
  // shows up in console as `[Player:idx] <source> N-1 → 0 (length=8)`. This
  // is the diagnostic we need to confirm/refute the "N-1 → N-2 → 0" symptom
  // at runtime. If the user reports a misjump, the log shows EXACTLY which
  // call site wrote the wrong value.
  // Pick the next playlist item index honoring the playlist's mode:
  //   - sequential: (current + 1) % length — the legacy behaviour
  //   - shuffle:    uniform random index, excluding `current` (so we don't
  //                 play the same item back-to-back); if there's only one
  //                 item that's a no-op anyway
  //   - weighted:   weighted random over items[].weight (defaulting weight=1
  //                 when missing); same back-to-back exclusion as shuffle
  //
  // Returns 0 for empty playlists (caller is expected to bounds-check).
  private pickNextIndex(currentIdx: number, length: number): number {
    if (length <= 0) return 0;
    if (length === 1) return 0;
    const mode = this.currentPlaylist?.mode ?? 'sequential';
    const items = this.currentPlaylist?.items ?? [];

    if (mode === 'shuffle') {
      // Uniform over [0, length), excluding currentIdx
      let pick = Math.floor(Math.random() * (length - 1));
      if (pick >= currentIdx) pick += 1;
      return pick;
    }

    if (mode === 'weighted') {
      // Build a cumulative weight table over items != currentIdx. An item
      // without a weight defaults to 1 so a partially-configured playlist
      // still behaves usefully (it becomes shuffle for those items).
      let total = 0;
      const cumulative: number[] = new Array(length);
      for (let i = 0; i < length; i++) {
        if (i === currentIdx) {
          cumulative[i] = total; // not eligible
          continue;
        }
        const w = items[i]?.weight ?? 1;
        total += w > 0 ? w : 1;
        cumulative[i] = total;
      }
      if (total <= 0) return (currentIdx + 1) % length; // degenerate: all zero
      const r = Math.random() * total;
      for (let i = 0; i < length; i++) {
        if (i === currentIdx) continue;
        if (r < cumulative[i]) return i;
      }
      return (currentIdx + 1) % length; // unreachable but defensive
    }

    // sequential (default)
    return (currentIdx + 1) % length;
  }

  private setCurrentItemIndex(next: number, source: string, length: number): void {
    const prev = this.currentItemIndex;
    this.currentItemIndex = next;
    // Use console.log directly so this survives prod-build log stripping.
    console.log(`[Player:idx] ${source} ${prev} → ${next} (length=${length})`);
    // Loud warning when the advance is non-monotonic (not +1 modulo length).
    // Expected advances are `prev + 1` (mod length) OR `prev` (no-op) OR 0
    // (skip-cooldown / playlist-switch reset). Anything else is a bug.
    const expectedAdvance = (prev + 1) % Math.max(1, length);
    if (next !== expectedAdvance && next !== 0 && next !== prev) {
      console.warn(
        `[Player:idx] UNEXPECTED — wrote ${next}, expected ${expectedAdvance} or 0. source=${source}`,
      );
    }
  }
  private isPlaying = false;
  private statusCallback: StatusCallback | null = null;
  private playTimer: ReturnType<typeof setTimeout> | null = null;
  private scheduleCheckInterval: ReturnType<typeof setInterval> | null = null;
  private wsConnection: WebSocket | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private wsReconnectDelay = 1000;
  private wsReconnectAttempts = 0;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private pongTimeout: ReturnType<typeof setTimeout> | null = null;

  // WebSocket constants
  private readonly WS_INITIAL_RECONNECT = 1000;
  private readonly WS_MAX_RECONNECT = 300000; // 5 min
  private readonly WS_BACKOFF_MULTIPLIER = 2.0;
  private readonly WS_PING_INTERVAL = 30000;
  private readonly WS_PONG_TIMEOUT = 10000;

  private useMultiZone = false; // Track if using multi-zone layout mode
  private useVideoWall = false; // Track if using video wall mode
  private currentOrientation = 0; // Track device orientation (0, 90, 180, 270)
  // Tracks whether the window-resize listener for re-applying the rotation
  // transform has been attached. Set to true on first call so we don't
  // attach the listener N times across manifest updates.
  private orientationResizeBound = false;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  private lastPlaybackTime = 0; // Track last successful playback
  // Bumped from 5min to 15min — signage videos can be longer than 5 min, and
  // a slow wait() is not the same as a hung chain. The new recoverFromFreeze
  // also skips entirely when isAdvancingItem=true, so this floor only kicks
  // in for genuinely stuck chains.
  private readonly WATCHDOG_TIMEOUT_MS = 900_000;
  // Hard kill threshold for chains that have been mid-advance the entire time
  // the watchdog has been polling. The pre-existing recoverFromFreeze() had a
  // blind spot: when isAdvancingItem=true it refreshed lastPlaybackTime so the
  // chain wouldn't be interrupted — but if the chain genuinely hung (a
  // wait()/renderAsset() promise that never resolved), the watchdog silenced
  // itself forever. Now we additionally track when the chain started
  // advancing; once it crosses 2× WATCHDOG_TIMEOUT_MS the chain is killed
  // regardless. Real signage videos top out around 60s, so 30 min mid-advance
  // is unambiguously a hung promise — not a slow but progressing chain.
  private readonly ADVANCE_HARD_KILL_MS = 1_800_000;
  // Reentrancy guard for playNextItem. Without this, two chains can advance
  // currentItemIndex in parallel (e.g., the recover-from-freeze path firing
  // while a normal advance is mid-flight), producing the "after-last shows
  // 2nd → last → 1st in milliseconds" symptom.
  private isAdvancingItem = false;
  // Timestamp at which the current advance chain started — set when
  // isAdvancingItem becomes true, cleared when it returns to false. Used by
  // recoverFromFreeze to detect genuinely hung chains; see comment on
  // ADVANCE_HARD_KILL_MS for the rationale.
  private advanceStartedAt = 0;
  private isPoweredOff = false; // Track if screen should be off due to power schedule
  private currentPowerStatus: PowerScheduleStatus | null = null;
  private isInBlackout = false; // Track if in schedule blackout (outside scheduled hours)
  /**
   * Track if in brightness-driven blackout (manifest.screenBrightness === 0,
   * usually because the operator pressed "Enable Blackout" on the dashboard).
   * Used to suppress play-event / proof-of-play reporting while a black
   * overlay covers the content — without this gate the player keeps
   * rendering invisible video and analytics/billing get inflated with
   * "plays" no human eye actually saw.
   */
  private isInBrightnessBlackout = false;
  private currentScheduleStatus: ScheduleStatus | null = null;
  private currentScheduleRuleId: string | undefined = undefined;

  // Infinite skip prevention
  private consecutiveSkips = 0;
  private readonly MAX_CONSECUTIVE_SKIPS = 10;
  private readonly SKIP_COOLDOWN_MS = 30000; // 30 seconds

  // Event handler references for cleanup
  private remoteCommandHandler: ((e: Event) => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;

  async initialize(statusCallback?: StatusCallback): Promise<boolean> {
    if (statusCallback) {
      this.statusCallback = statusCallback;
    }

    this.updateStatus({ status: 'loading', message: 'Initializing...' });
    console.log('[Player] Starting initialization...');

    try {
      // Initialize fallback manager for offline support
      await fallbackManager.initialize((state: FallbackState) => {
        console.log('[Player] Fallback state changed:', state);
        if (state.isUsingFallback) {
          this.updateStatus({
            status: 'offline',
            message: `Using fallback content: ${state.reason}`,
          });
        }
      });

      // Load or create device registration
      this.registration = await getDeviceRegistration() ?? null;
      console.log('[Player] Device registration:', this.registration ? 'Found' : 'Not found');

      if (!this.registration) {
        // Check URL for pairing code
        const params = new URLSearchParams(window.location.search);
        const pairingCode = params.get('code');

        if (pairingCode) {
          await this.pairDevice(pairingCode);
        } else {
          this.updateStatus({
            status: 'error',
            message: 'Device not registered. Please scan the pairing code.',
          });
          return false;
        }
      }

      if (!this.registration) {
        return false;
      }

      // Set up API client with registration
      api.setRegistration(this.registration);

      // Start event reporter early so boot cleanup events are reported
      eventReporter.start();

      // Load cached manifest for boot-time asset cleanup (deleteCachedAsset
      // of any asset IDs no longer in the cached manifest). We keep the
      // returned value as a fallback if the fresh fetch fails.
      //
      // Previously we used a "fast-path" that started playing the cached
      // manifest immediately while fetching a fresh one in background,
      // then swapped via onManifestUpdate when the fresh one arrived.
      // That race produced a visible "old playlist plays then switches"
      // jump on every cold boot. For signage, 0.5–2s of extra boot time
      // is acceptable; a visible mid-playback content switch is not.
      console.log('[Player] Loading manifest (waiting for fresh)...');
      const cachedManifest = await manifestLoader.loadCached();

      let manifest: DeviceManifest | null = await manifestLoader.initialize();

      if (!manifest && cachedManifest) {
        // Fresh fetch failed (network down on boot). Use cached so the
        // panel still shows something. The no-cached fallback path
        // (fallbackManager.getLastGoodManifest) handles the rest below.
        console.log('[Player] Fresh manifest fetch failed, falling back to cached');
        manifest = cachedManifest;
      }

      if (!manifest) {
        console.error('[Player] No manifest available');

        // Try to get last good manifest from fallback manager
        const lastGoodManifest = fallbackManager.getLastGoodManifest();
        if (lastGoodManifest) {
          console.log('[Player] Using last good manifest from fallback manager');
          manifest = lastGoodManifest;
          fallbackManager.enterFallbackMode('network_offline');
        } else {
          // Show emergency content
          this.updateStatus({
            status: 'offline',
            message: 'No manifest available. Showing emergency content.',
          });

          // Render emergency content
          const container = document.getElementById('player-container');
          if (container) {
            fallbackManager.renderEmergencyContent(container);
          }
          return false;
        }
      } else {
        // Save as last good manifest
        await fallbackManager.saveGoodManifest(manifest);
      }

      console.log('[Player] Manifest received:', {
        version: manifest.version,
        playlists: manifest.playlists?.length || 0,
        assets: manifest.assets?.length || 0,
        hasLayout: !!manifest.layout,
        activePlaylistId: manifest.schedule?.activePlaylistId,
      });

      // Debug: log full layout structure if present
      if (manifest.layout) {
        console.log('[Player] Layout details:', {
          id: manifest.layout.id,
          name: manifest.layout.name,
          dimensions: `${manifest.layout.width}x${manifest.layout.height}`,
          zonesCount: manifest.layout.zones?.length || 0,
          zones: manifest.layout.zones?.map(z => ({
            id: z.id,
            name: z.name,
            contentType: z.contentType,
            hasContent: !!z.content,
            position: `${z.x},${z.y} ${z.width}x${z.height}`,
          })),
        });
      } else {
        console.log('[Player] No layout in manifest - will use single-zone mode');
      }

      // Apply device orientation (CSS rotation) before rendering content
      this.applyDeviceOrientation(manifest);

      // Apply screen brightness on initial boot too. Without this, a player
      // that comes online with a manifest already containing
      // `screenBrightness: 0` (because the operator pressed Blackout while
      // the device was offline / restarting) would render content
      // normally — `applyScreenBrightness` was only wired into
      // `onManifestUpdate` which only fires on subsequent version changes.
      this.applyScreenBrightness(manifest.screenBrightness ?? null);

      // Set manifest in scheduler and analytics
      scheduler.setManifest(manifest);
      analytics.setManifestVersion(manifest.version);

      // Start power schedule monitoring
      scheduler.startPowerScheduleMonitor((status) => {
        this.onPowerScheduleChange(status);
      });

      // Start schedule monitoring (for blackout when outside scheduled hours)
      scheduler.startScheduleMonitor((status) => {
        this.onScheduleStatusChange(status);
      });

      // Check if device is part of a video wall
      this.useVideoWall = videoWallManager.initialize(manifest);
      console.log('[Player] Video wall mode:', this.useVideoWall);

      // Initialize sync manager for video wall or screen group
      if (manifest.videoWall) {
        syncManager.initializeForVideoWall(manifest.videoWall);
      } else if (manifest.screenGroup) {
        syncManager.initializeForScreenGroup(manifest.screenGroup);
      }

      // Check if manifest has a layout - use multi-zone mode
      this.useMultiZone = !!manifest.layout;
      console.log('[Player] Multi-zone mode:', this.useMultiZone);

      if (this.useMultiZone) {
        // Initialize layout manager for multi-zone support
        console.log('Multi-zone mode: Using LayoutManager');
        layoutManager.initialize('player-container');
        await layoutManager.setManifest(manifest);
        layoutManager.setPlayEventCallback((event) => {
          if (this.isInBrightnessBlackout) return;
          analytics.recordPlayEvent(event);
        });
        await layoutManager.setLayout(manifest.layout || null);
      } else {
        // Legacy single-zone mode
        console.log('Single-zone mode: Using legacy renderer');
        renderer.initialize('player-container');
        renderer.setPlayEventCallback((event) => {
          if (this.isInBrightnessBlackout) return;
          analytics.recordPlayEvent(event);
        });
      }

      // Initialize overlay renderer for promotional overlays
      overlayRenderer.initialize('player-container');
      if (manifest.overlays && manifest.overlays.length > 0) {
        console.log('[Player] Rendering', manifest.overlays.length, 'overlays');
        overlayRenderer.setOverlays(manifest.overlays as OverlayConfig[]);
      }

      // Start auto-refresh
      manifestLoader.startAutoRefresh((newManifest) => {
        this.onManifestUpdate(newManifest);
      });

      // Connect WebSocket
      this.connectWebSocket();

      // Start analytics
      await analytics.start();

      // Listen for remote commands from heartbeat response
      this.remoteCommandHandler = ((e: Event) => {
        const cmd = (e as CustomEvent).detail as { type: string; data?: Record<string, string> };
        console.log('[Player] Remote command received:', cmd.type);
        switch (cmd.type) {
          case 'RESTART':
            location.reload();
            break;
          case 'REFRESH':
            manifestLoader.refresh().catch((error) => {
              console.error('Manifest refresh failed:', error);
            });
            break;
          case 'CLEAR_CACHE':
            clearAllData().then(() => location.reload());
            break;
          default:
            console.log('[Player] Unknown remote command:', cmd.type);
        }
      });
      window.addEventListener('remote-command', this.remoteCommandHandler);

      // Report manifest sync (event reporter already started earlier)
      eventReporter.reportManifestSync(manifest.version);

      // Listen for network changes
      this.onlineHandler = () => {
        console.log('[Player] Network restored');
        eventReporter.reportNetworkRestored();
      };
      this.offlineHandler = () => {
        console.log('[Player] Network lost');
        eventReporter.reportNetworkLost();
      };
      window.addEventListener('online', this.onlineHandler);
      window.addEventListener('offline', this.offlineHandler);

      // Report device profile
      await this.reportDeviceProfile();

      return true;
    } catch (error) {
      console.error('Failed to initialize player:', error);
      const errorMsg = error instanceof Error ? error.message : 'Initialization failed';

      this.updateStatus({
        status: 'error',
        message: errorMsg,
      });

      // Re-throw auth errors so main.ts can handle them (clear data and re-pair)
      if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        throw error;
      }

      return false;
    }
  }

  private async pairDevice(pairingCode: string): Promise<void> {
    try {
      const response = await fetch(`${config.baseUrl}/device/pair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pairingCode,
          deviceInfo: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            screenWidth: screen.width,
            screenHeight: screen.height,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Pairing failed');
      }

      const data = await response.json();

      this.registration = {
        deviceId: data.deviceId,
        deviceSecret: data.deviceSecret,
        brandId: data.brandId,
        storeId: data.storeId,
        zoneId: data.zoneId,
      };

      await setDeviceRegistration(this.registration);

      // Clear pairing code from URL
      window.history.replaceState({}, '', window.location.pathname);
    } catch (error) {
      console.error('Pairing failed:', error);
      throw error;
    }
  }

  async start(): Promise<void> {
    if (this.isPlaying) return;

    this.isPlaying = true;
    this.lastPlaybackTime = Date.now();
    analytics.setState('ONLINE');
    analytics.setPlaying(true);

    // Start watchdog timer to detect freezes
    this.startWatchdog();

    if (this.useMultiZone) {
      // Multi-zone mode - layout manager handles all playback
      console.log('Starting multi-zone playback');
      await layoutManager.startPlayback();
      this.updateStatus({ status: 'playing', message: 'Multi-zone playback' });
    } else {
      // Legacy single-zone mode
      // Start schedule checking
      this.scheduleCheckInterval = setInterval(() => {
        this.checkSchedule();
      }, 60000); // Check every minute

      // Start playback
      await this.checkSchedule();
      await this.playNextItem();
    }
  }

  async stop(): Promise<void> {
    this.isPlaying = false;
    analytics.setPlaying(false);

    if (this.playTimer) {
      clearTimeout(this.playTimer);
      this.playTimer = null;
    }

    if (this.scheduleCheckInterval) {
      clearInterval(this.scheduleCheckInterval);
      this.scheduleCheckInterval = null;
    }

    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }

    if (this.useMultiZone) {
      layoutManager.stop();
    } else {
      renderer.clear();
    }
  }

  /**
   * Watchdog timer to detect frozen playback and auto-recover
   */
  private startWatchdog(): void {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
    }

    this.watchdogTimer = setInterval(() => {
      const timeSinceLastPlayback = Date.now() - this.lastPlaybackTime;

      if (this.isPlaying && timeSinceLastPlayback > this.WATCHDOG_TIMEOUT_MS) {
        console.error('[Watchdog] Playback appears frozen! Last activity:',
          Math.round(timeSinceLastPlayback / 1000), 'seconds ago. Restarting...');

        // Report watchdog trigger to heartbeat and event history
        analytics.setWatchdogTriggered(true);
        eventReporter.reportEventImmediate('WARNING', {
          warning: 'Watchdog triggered — playback frozen',
          stuckDurationSeconds: Math.round(timeSinceLastPlayback / 1000).toString(),
        });

        // Force restart playback
        this.recoverFromFreeze();
      }
    }, 60000); // Check every minute
  }

  /**
   * Recover from a frozen state
   */
  private async recoverFromFreeze(): Promise<void> {
    // If a chain is actively advancing (in wait, in renderer playback, in
    // image render), the watchdog must NOT clear timers or release the
    // advancing guard — that disrupts the in-flight chain and was the cause
    // of "sometimes goes directly without waiting full duration". A slow
    // chain is not a hung chain.
    //
    // BUT: a chain that's been "advancing" for longer than ADVANCE_HARD_KILL_MS
    // is no longer slow — it's deadlocked on a promise that will never settle
    // (a wait() pre-empted by a paused tab, a renderAsset() whose play()
    // promise vanished, etc.). The previous build returned here unconditionally
    // and refreshed lastPlaybackTime, which silenced the watchdog forever and
    // left genuinely-stuck devices stuck for hours. We now break the chain
    // only after the hard kill threshold; below it, behave as before.
    const advanceAge = this.advanceStartedAt > 0
      ? Date.now() - this.advanceStartedAt
      : 0;
    if (this.isAdvancingItem && advanceAge < this.ADVANCE_HARD_KILL_MS) {
      console.warn(
        `[Watchdog] Chain mid-advance for ${Math.round(advanceAge / 1000)}s (< hard-kill ${Math.round(this.ADVANCE_HARD_KILL_MS / 1000)}s) — refreshing timestamp only.`,
      );
      this.lastPlaybackTime = Date.now();
      return;
    }
    if (this.isAdvancingItem) {
      console.error(
        `[Watchdog] Chain mid-advance for ${Math.round(advanceAge / 1000)}s — past hard-kill threshold. Forcing recovery.`,
      );
      // Report the hard-kill so it shows up in event history / dashboard
      // audit alongside the routine watchdog warning. Helps spot a player
      // that's hitting this path repeatedly (a sign of a deeper hang in
      // renderAsset / wait, not a transient blip).
      eventReporter.reportEventImmediate('WARNING', {
        warning: 'Watchdog hard-kill — chain stuck mid-advance',
        stuckDurationSeconds: Math.round(advanceAge / 1000).toString(),
      });
    } else {
      console.log('[Watchdog] Attempting recovery (no chain in flight)...');
    }

    try {
      // Now safe to clear: no chain is using this.playTimer.
      if (this.playTimer) {
        clearTimeout(this.playTimer);
        this.playTimer = null;
      }

      // Refresh the watchdog timestamp so we don't loop. Do NOT reset
      // currentItemIndex — resetting to 0 races with any new chain and
      // produces the "after-last → 2nd → last → 1st in milliseconds" symptom.
      this.lastPlaybackTime = Date.now();
      // Release the reentrancy guard in case a previous chain crashed (or
      // hung past the hard-kill threshold) before clearing it.
      this.isAdvancingItem = false;
      this.advanceStartedAt = 0;

      if (this.useMultiZone) {
        // Restart multi-zone playback
        layoutManager.stop();
        await layoutManager.startPlayback();
      } else {
        // Restart single-zone playback at the current item — no index reset.
        renderer.clear();
        await this.checkSchedule();
        await this.playNextItem();
      }

      console.log('[Watchdog] Recovery successful');
    } catch (error) {
      console.error('[Watchdog] Recovery failed:', error);
      // Last resort: reload the page
      console.log('[Watchdog] Forcing page reload...');
      window.location.reload();
    }
  }

  private async checkSchedule(): Promise<void> {
    const newPlaylist = scheduler.getActivePlaylist();

    if (!newPlaylist) {
      console.warn('No active playlist');
      return;
    }

    // Three cases to handle:
    //  (1) Different playlist id  → switch and reset to item 0.
    //  (2) Same id, items list changed (added/removed/reordered) → reload
    //      currentPlaylist so the new items take effect on the NEXT advance.
    //      Without this, a playlist edit (e.g. "insert an image between two
    //      videos") was being ignored — the player's in-memory item list
    //      stayed at the pre-edit size, even though the manifest version
    //      bumped and the device reported the new version in heartbeat.
    //  (3) Same id, same items list → no-op.
    if (this.currentPlaylist?.id !== newPlaylist.id) {
      console.log(`Switching to playlist: ${newPlaylist.id}`);
      this.currentPlaylist = newPlaylist;
      this.setCurrentItemIndex(0, 'playlist-switch', newPlaylist.items.length);
      return;
    }

    const oldItemIds = this.currentPlaylist.items.map((i) => i.id);
    const newItemIds = newPlaylist.items.map((i) => i.id);
    const itemsChanged =
      oldItemIds.length !== newItemIds.length ||
      oldItemIds.some((id, i) => id !== newItemIds[i]);

    if (itemsChanged) {
      console.log(
        `Playlist ${newPlaylist.id} items changed (${oldItemIds.length} -> ${newItemIds.length}), reloading`,
      );
      this.currentPlaylist = newPlaylist;
      // Clamp the index rather than always resetting to 0 — if items were
      // only appended, we'd otherwise rewind to the start mid-loop, which
      // is jarring. Resetting only when the new list is shorter or the
      // current index is past the new length.
      const safeIndex = Math.min(this.currentItemIndex, newPlaylist.items.length - 1);
      this.setCurrentItemIndex(
        Math.max(0, safeIndex),
        'playlist-items-changed',
        newPlaylist.items.length,
      );
    }
  }

  /**
   * Handle power schedule changes
   */
  private async onPowerScheduleChange(status: PowerScheduleStatus): Promise<void> {
    console.log('[Player] Power schedule status:', status);
    // Surface to platform shells (webOS, Tizen) so they can drive OS-level
    // display power (tvpower turnOnScreen/turnOffScreen) — independent of the
    // in-app blackout overlay the player handles below.
    window.dispatchEvent(new CustomEvent('govista-power-schedule-change', { detail: status }));
    this.currentPowerStatus = status;

    const wasPoweredOff = this.isPoweredOff;
    this.isPoweredOff = !status.shouldBeOn;

    if (status.reason === 'power_off') {
      // Screen should be completely off
      if (!wasPoweredOff && this.isPoweredOff) {
        console.log('[Player] Power schedule: Screen should be OFF');
        this.updateStatus({ status: 'paused', message: 'Screen powered off by schedule' });
        await this.stop();
        this.showPowerOffScreen();
      }
    } else if (status.reason === 'outside_working_hours') {
      // Screen is on but outside working hours - could show idle content
      console.log('[Player] Power schedule: Outside working hours');
      this.updateStatus({ status: 'playing', message: 'Outside working hours' });
    } else if (status.reason === 'active' || status.reason === 'no_schedule') {
      // Screen should be on and playing content
      if (wasPoweredOff && !this.isPoweredOff) {
        console.log('[Player] Power schedule: Screen should be ON');
        this.hidePowerOffScreen();
        await this.start();
      }
    }
  }

  /**
   * Handle schedule status changes (entering/exiting blackout based on schedule rules)
   */
  private async onScheduleStatusChange(status: ScheduleStatus): Promise<void> {
    console.log('[Player] Schedule status changed:', status);
    this.currentScheduleStatus = status;
    this.currentScheduleRuleId = status.currentRule?.ruleId;

    const wasInBlackout = this.isInBlackout;
    this.isInBlackout = !status.inSchedule;

    if (!status.inSchedule && !wasInBlackout) {
      // Entering blackout - outside scheduled hours
      console.log('[Player] Entering blackout mode (outside schedule hours)');
      this.updateStatus({ status: 'paused', message: 'Outside scheduled hours - blackout' });
      await this.stop();
      this.showBlackoutScreen();
      eventReporter.reportBlackoutEnter();
    } else if (status.inSchedule && wasInBlackout) {
      // Exiting blackout - within scheduled hours
      console.log('[Player] Exiting blackout mode (within schedule hours)');
      this.hideBlackoutScreen();
      await this.start();
      eventReporter.reportBlackoutExit();
      if (status.activePlaylistId) {
        eventReporter.reportScheduleStart(status.activePlaylistId, status.currentRule?.ruleId);
      }
    }
  }

  /**
   * Show blackout screen when outside scheduled hours
   */
  private showBlackoutScreen(): void {
    const container = document.getElementById('player-container');
    if (!container) return;

    const overlay = this.ensureBlackoutOverlay();
    overlay.style.display = 'flex';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.opacity = '1';

    if (this.currentScheduleStatus?.currentRule) {
      overlay.innerHTML = `
        <div style="color: #222; text-align: center; font-family: sans-serif;">
          <div style="font-size: 12px; opacity: 0.5;">Program dışı</div>
        </div>
      `;
    }
  }

  /**
   * Hide the blackout screen
   */
  private hideBlackoutScreen(): void {
    const overlay = document.getElementById('blackout-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
  }

  /**
   * Show a black/off screen when powered off by schedule
   */
  private showPowerOffScreen(): void {
    const container = document.getElementById('player-container');
    if (!container) return;

    // Create or get power-off overlay
    let overlay = document.getElementById('power-off-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'power-off-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: black;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
      `;
      document.body.appendChild(overlay);
    }

    overlay.style.display = 'flex';

    // Optional: Show next power-on time
    if (this.currentPowerStatus?.nextPowerChange) {
      const nextOn = this.currentPowerStatus.nextPowerChange;
      overlay.innerHTML = `
        <div style="color: #333; text-align: center; font-family: sans-serif;">
          <div style="font-size: 14px;">Screen will turn on at</div>
          <div style="font-size: 24px; margin-top: 10px;">${nextOn.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
      `;
    }
  }

  /**
   * Hide the power-off screen
   */
  private hidePowerOffScreen(): void {
    const overlay = document.getElementById('power-off-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
  }

  private async playNextItem(): Promise<void> {
    if (!this.isPlaying) return;

    // Reentrancy guard — only one chain at a time. The natural advance path
    // schedules the next call via `playTimer = setTimeout(..., 0)` so the
    // outer playNextItem unwinds (and clears this flag) before the next
    // invocation starts. Reentrant callers (recoverFromFreeze, stale
    // playTimer from a previous error branch) coalesce here instead of
    // racing the index forward.
    if (this.isAdvancingItem) {
      console.warn('[Player] playNextItem reentrant call — coalescing');
      return;
    }
    this.isAdvancingItem = true;
    this.advanceStartedAt = Date.now();
    // Always clear any pending advance timer at the top of a fresh chain —
    // a new chain supersedes the previous one.
    if (this.playTimer) {
      clearTimeout(this.playTimer);
      this.playTimer = null;
    }

    try {
      await this.playNextItemBody();
    } finally {
      this.isAdvancingItem = false;
      this.advanceStartedAt = 0;
    }
  }

  // Internal body — the actual playback loop. Always called via playNextItem
  // (which owns the reentrancy guard). Direct callers are the test of bad code.
  private async playNextItemBody(): Promise<void> {
    if (!this.isPlaying) return;

    // Re-assert the device orientation transform at the start of every chain
    // iteration. Several downstream paths wipe player-container.style.cssText
    // (layoutManager.initialize, the layout-mode swap inside onManifestUpdate,
    // some renderer reinit paths) and would silently strip the rotation we
    // set up at boot. Without this guard, a portrait device that hits any of
    // those paths reverts to landscape rendering — content shows letterboxed
    // in a narrow centered strip until the next manifest version change.
    // Cheap: applyDeviceOrientation is a few inline style writes when the
    // transform is already correct.
    const liveManifest = manifestLoader.getManifest();
    if (liveManifest) {
      this.applyDeviceOrientation(liveManifest, true);
    }

    // Infinite skip prevention — if too many consecutive errors, pause and retry
    if (this.consecutiveSkips >= this.MAX_CONSECUTIVE_SKIPS) {
      console.error(`[Player] ${this.consecutiveSkips} consecutive skips reached, entering cooldown (${this.SKIP_COOLDOWN_MS / 1000}s)...`);
      eventReporter.reportError('SKIP_LOOP', `All assets failed after ${this.consecutiveSkips} consecutive skips`);
      analytics.setError('SKIP_LOOP', 'All assets failed — entering cooldown');
      this.updateStatus({ status: 'error', message: 'All content failed to play. Retrying in 30s...' });
      this.showSkipCooldownOverlay();
      await new Promise(r => { this.playTimer = setTimeout(r, this.SKIP_COOLDOWN_MS); });
      this.hideSkipCooldownOverlay();
      this.consecutiveSkips = 0;
      this.setCurrentItemIndex(0, 'skip-cooldown-reset', this.currentPlaylist?.items.length ?? 0);
      analytics.setError(undefined, undefined);
    }

    // No active playlist assigned to this device
    if (!this.currentPlaylist) {
      console.warn('[Player] No playlist assigned to this device');
      this.updateStatus({ status: 'no_content', message: 'No content assigned to this device' });
      return;
    }

    const items = this.currentPlaylist.items;
    const assets = this.currentPlaylist.assets;
    console.log('[Player] Playing playlist:', this.currentPlaylist.id, 'items:', items.length, 'assets:', assets.size, 'idx:', this.currentItemIndex);

    if (items.length === 0) {
      console.warn('[Player] Playlist is empty');
      this.updateStatus({ status: 'no_content', message: 'No content assigned to this device' });
      return;
    }

    // Defensive bounds check. If currentItemIndex is somehow out of range
    // for the current items array (manifest swapped under us, prior chain
    // crashed mid-write, etc.) reset to 0 with a loud warning rather than
    // crashing on undefined item access below.
    if (this.currentItemIndex < 0 || this.currentItemIndex >= items.length) {
      console.warn(`[Player:idx] currentItemIndex ${this.currentItemIndex} out of range for length ${items.length} — resetting to 0`);
      this.setCurrentItemIndex(0, 'oob-reset', items.length);
    }

    // Get current item
    const item = items[this.currentItemIndex];
    console.log('[Player] Playing item', this.currentItemIndex, ':', {
      itemId: item.id,
      assetId: item.assetId,
      layoutId: item.layoutId,
      designId: item.designId,
      duration: item.duration,
    });

    // Handle layout items — render multi-zone layout for item's duration
    if (item.layoutId) {
      const manifest = manifestLoader.getManifest();
      const layout = manifest?.layouts?.find((l) => l.id === item.layoutId);

      if (!layout || !manifest) {
        console.warn('[Player] Layout not found for item:', item.layoutId);
        this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);
        if (this.isPlaying) {
          this.playTimer = setTimeout(() => this.playNextItem(), 1000);
        }
        return;
      }

      this.updateStatus({
        status: 'playing',
        playlist: this.currentPlaylist.id,
        message: `Layout: ${layout.name}`,
      });

      try {
        console.log('[Player] Rendering layout item:', layout.name);
        const cleanup = await layoutManager.renderTemporaryLayout(layout, manifest, 'player-container');

        this.lastPlaybackTime = Date.now();

        const durationMs = this.sanitizeDurationMs(item.duration, 'layout');
        console.log('[Player] Waiting for layout duration:', durationMs, 'ms (item.duration=', item.duration, ')');
        await this.wait(durationMs);

        cleanup();

        this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);
        if (this.isPlaying) {
          // Defer so the outer playNextItem unwinds (clears isAdvancingItem)
          // before the next chain starts — prevents reentrant race.
          this.playTimer = setTimeout(() => this.playNextItem(), 0);
        }
      } catch (error) {
        console.error('[Player] Error playing layout item:', error);
        this.lastPlaybackTime = Date.now();
        this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);
        if (this.isPlaying) {
          this.playTimer = setTimeout(() => this.playNextItem(), 1000);
        }
      }
      return;
    }

    // Handle design items — render full-screen design for item's duration
    if (item.designId) {
      const manifest = manifestLoader.getManifest();
      const design = manifest?.designs?.find((d) => d.id === item.designId);

      if (!design || !manifest) {
        console.warn('[Player] Design not found for item:', item.designId);
        this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);
        if (this.isPlaying) {
          this.playTimer = setTimeout(() => this.playNextItem(), 1000);
        }
        return;
      }

      this.updateStatus({
        status: 'playing',
        playlist: this.currentPlaylist.id,
        message: `Design: ${design.name}`,
      });

      try {
        console.log('[Player] Rendering design item:', design.name);

        // Create a temporary full-screen container for the design
        const playerContainer = document.getElementById('player-container');
        if (!playerContainer) {
          throw new Error('Player container not found');
        }

        const tempContainer = document.createElement('div');
        tempContainer.id = `temp-design-${design.id}`;
        // Build the design DOM with the container hidden, then reveal in a
        // single paint after all layers/widgets are added. Without this the
        // user sees the black background + partially-rendered layers for
        // 1-2 frames between the previous content and the design — the
        // "1-second flash before design" symptom.
        tempContainer.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 10;
          overflow: hidden;
          background: #000;
          visibility: hidden;
        `;
        playerContainer.appendChild(tempContainer);

        // Create a content container scaled to design dimensions
        const contentContainer = document.createElement('div');
        contentContainer.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
        `;

        // Set design background
        if (design.background) {
          if (design.background.type === 'color' || design.background.type === 'solid') {
            contentContainer.style.backgroundColor = design.background.value;
          } else if (design.background.type === 'gradient') {
            contentContainer.style.background = design.background.value;
          } else if (design.background.type === 'image') {
            contentContainer.style.backgroundImage = `url(${design.background.value})`;
            contentContainer.style.backgroundSize = 'cover';
            contentContainer.style.backgroundPosition = 'center';
          }
        }
        tempContainer.appendChild(contentContainer);

        // Compute font scale: design pixels → screen pixels
        // Use container dimensions to scale text proportionally
        const containerRect = tempContainer.getBoundingClientRect();
        const scaleX = containerRect.width / design.width;
        const scaleY = containerRect.height / design.height;
        const fontScale = Math.min(scaleX, scaleY);

        // Render design layers scaled to fill the screen
        const sortedLayers = [...design.layers].sort((a, b) => a.zIndex - b.zIndex);
        for (const layer of sortedLayers) {
          if (layer.visible === false) continue;

          const wrapper = document.createElement('div');
          wrapper.className = `layer layer-${layer.type}`;
          // Convert pixel positions to percentages based on design dimensions
          const leftPct = (layer.x / design.width) * 100;
          const topPct = (layer.y / design.height) * 100;
          const widthPct = (layer.width / design.width) * 100;
          const heightPct = (layer.height / design.height) * 100;
          wrapper.style.cssText = `
            position: absolute;
            left: ${leftPct}%;
            top: ${topPct}%;
            width: ${widthPct}%;
            height: ${heightPct}%;
            ${layer.rotation ? `transform: rotate(${layer.rotation}deg);` : ''}
            ${layer.opacity !== undefined ? `opacity: ${layer.opacity};` : ''}
          `;

          // Layer properties come from layer.props (DB schema format)
          const props = (layer as any).props || {};

          if (layer.type === 'text') {
            const text = props.text || props.content || '';
            const color = props.color || '#ffffff';
            const rawFontSize = props.fontSize || 16;
            const fontSize = rawFontSize * fontScale;
            const fontWeight = props.fontWeight || 'normal';
            const fontFamily = props.fontFamily || 'sans-serif';
            const fontStyle = props.fontStyle || 'normal';
            const textAlign = props.textAlign || 'left';
            const lineHeight = props.lineHeight || 1.2;
            const letterSpacing = (props.letterSpacing || 0) * fontScale;
            const verticalAlign = props.verticalAlign || 'top';
            wrapper.innerHTML = `<div style="
              font-family: ${fontFamily};
              font-size: ${fontSize}px;
              font-weight: ${fontWeight};
              font-style: ${fontStyle};
              color: ${color};
              text-align: ${textAlign};
              line-height: ${lineHeight};
              letter-spacing: ${letterSpacing}px;
              width: 100%;
              height: 100%;
              display: flex;
              align-items: ${verticalAlign === 'bottom' ? 'flex-end' : verticalAlign === 'middle' ? 'center' : 'flex-start'};
              justify-content: ${textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'};
            ">${text}</div>`;
          } else if (layer.type === 'image') {
            const img = document.createElement('img');
            img.alt = '';
            img.draggable = false;
            img.style.cssText = `width: 100%; height: 100%; object-fit: ${props.objectFit || 'contain'}; opacity: 0; transition: opacity 0.3s;`;
            img.onload = () => { img.style.opacity = '1'; };
            img.src = props.src || '';
            wrapper.appendChild(img);
          } else if (layer.type === 'shape') {
            const shape = document.createElement('div');
            shape.style.cssText = `
              width: 100%; height: 100%;
              background: ${props.fill || props.fillColor || 'transparent'};
              ${props.stroke || props.strokeColor ? `border: ${props.strokeWidth || 1}px solid ${props.stroke || props.strokeColor};` : ''}
              ${props.cornerRadius || props.borderRadius ? `border-radius: ${props.cornerRadius || props.borderRadius}px;` : ''}
              ${props.shapeType === 'circle' ? 'border-radius: 50%;' : ''}
            `;
            wrapper.appendChild(shape);
          } else if (layer.type === 'video') {
            const video = document.createElement('video');
            video.src = props.src || '';
            video.autoplay = true;
            video.loop = props.loop !== false;
            video.muted = props.muted !== false;
            video.playsInline = true;
            video.style.cssText = `
              width: 100%; height: 100%;
              object-fit: ${props.objectFit || 'contain'};
              ${props.borderRadius ? `border-radius: ${props.borderRadius}px;` : ''}
            `;
            wrapper.appendChild(video);
          } else if (layer.type === 'widget') {
            const widgetType = props.widgetType || '';
            const widgetContainer = document.createElement('div');
            widgetContainer.style.cssText = `
              width: 100%; height: 100%;
              display: flex; align-items: center; justify-content: center;
              color: #fff;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            `;

            let widget: Widget | null = null;
            // Pass props directly as config (editor stores config fields flat in props)
            const widgetConfig = { ...props };

            switch (widgetType) {
              case 'clock':
                widget = new ClockWidget({
                  use24h: widgetConfig.format !== '12h',
                  showSeconds: widgetConfig.showSeconds !== false,
                  showDate: widgetConfig.showDate === true,
                  timezone: widgetConfig.timezone as string | undefined,
                  fontSize: widgetConfig.fontSize ? (widgetConfig.fontSize as number) * fontScale : 48 * fontScale,
                  color: widgetConfig.color as string || '#ffffff',
                });
                break;
              case 'weather':
                widget = new WeatherWidget({
                  city: widgetConfig.location as string || 'Istanbul',
                  unit: (widgetConfig.unit as 'celsius' | 'fahrenheit') || 'celsius',
                  fontSize: widgetConfig.fontSize ? (widgetConfig.fontSize as number) * fontScale : 48 * fontScale,
                  color: widgetConfig.color as string || '#ffffff',
                });
                break;
              case 'qr':
                widget = new QRCodeWidget({
                  content: widgetConfig.content as string || 'https://govista.app',
                  size: widgetConfig.size as number || 200,
                  foreground: widgetConfig.foreground as string || '#000000',
                  background: widgetConfig.background as string || '#ffffff',
                  darkColor: widgetConfig.foreground as string || '#000000',
                  lightColor: widgetConfig.background as string || '#ffffff',
                  label: widgetConfig.label as string || undefined,
                  dotStyle: (widgetConfig.dotStyle as string as any) || 'squares',
                  cornerStyle: (widgetConfig.cornerStyle as string as any) || 'square',
                  cornerDotStyle: (widgetConfig.cornerDotStyle as string as any) || 'square',
                  cornerColor: widgetConfig.cornerColor as string || undefined,
                });
                break;
              case 'rss':
                widget = new RSSWidget(widgetConfig as any);
                break;
              default:
                widgetContainer.innerHTML = `<div style="opacity: 0.5; font-size: 14px;">${widgetType} widget</div>`;
            }

            if (widget) {
              const widgetEl = widget.render();
              widgetContainer.appendChild(widgetEl);
              // Store widget reference for cleanup
              if (!(tempContainer as any)._widgets) (tempContainer as any)._widgets = [];
              (tempContainer as any)._widgets.push(widget);
            }
            wrapper.appendChild(widgetContainer);
          }

          contentContainer.appendChild(wrapper);
        }

        this.lastPlaybackTime = Date.now();

        // Reveal the design in a single paint, AFTER the browser has had a
        // chance to lay out all layers/widgets. Double rAF: first frame
        // applies pending DOM mutations; second frame guarantees they're
        // painted. Then flip visibility — the previous content stays on
        // screen until exactly this moment, eliminating the black flash.
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });
        tempContainer.style.visibility = 'visible';

        const durationMs = this.sanitizeDurationMs(item.duration, 'design');
        console.log('[Player] Waiting for design duration:', durationMs, 'ms (item.duration=', item.duration, ')');
        await this.wait(durationMs);

        // Stop widgets + videos immediately so they don't keep running during
        // the fade-out. Widget destruction has no visual effect on the
        // tempContainer itself.
        if ((tempContainer as any)._widgets) {
          for (const w of (tempContainer as any)._widgets) {
            try { w.destroy(); } catch {}
          }
        }
        tempContainer.querySelectorAll('video').forEach((v) => {
          v.pause();
          v.src = '';
        });

        // Schedule the next chain NOW so its renderAsset starts in parallel
        // with the design overlay fade-out below. This is critical for the
        // "item N-1 briefly flashes between the last design and the first
        // asset" symptom: the renderer's old currentElement (the asset that
        // played BEFORE this design) is still at z-index 0. If we remove
        // tempContainer immediately, that old element is exposed for the
        // ~500ms the renderer needs to fade in the new asset. Instead, we
        // keep the design overlay in place (at decreasing opacity) while
        // the renderer transitions underneath. The design covers the swap.
        this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);
        if (this.isPlaying) {
          // Defer so the outer playNextItem unwinds (clears isAdvancingItem)
          // before the next chain starts — prevents reentrant race.
          this.playTimer = setTimeout(() => this.playNextItem(), 0);
        }

        // Crossfade the design overlay out (matches the renderer's 500ms
        // default transition). The renderer's renderAsset for the next item
        // fades its element in (z-index 1, above the old z-index 0 asset);
        // simultaneously the design (z-index 10, above everything) fades out
        // — so the user sees a clean design → next-asset transition with no
        // intermediate flash. Use a standalone setTimeout (not playTimer) so
        // it can't be cleared by the next chain's prologue.
        const FADE_OUT_MS = 500;
        tempContainer.style.transition = `opacity ${FADE_OUT_MS}ms ease-in-out`;
        tempContainer.style.opacity = '0';
        setTimeout(() => {
          try { tempContainer.remove(); } catch { /* already gone */ }
        }, FADE_OUT_MS + 100);
      } catch (error) {
        console.error('[Player] Error playing design item:', error);
        this.lastPlaybackTime = Date.now();
        // Clean up on error
        const tempEl = document.getElementById(`temp-design-${design.id}`);
        if (tempEl) tempEl.remove();
        this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);
        if (this.isPlaying) {
          this.playTimer = setTimeout(() => this.playNextItem(), 1000);
        }
      }
      return;
    }

    // Handle asset items (existing behavior)
    const asset = item.assetId ? assets.get(item.assetId) : undefined;

    if (!asset) {
      console.warn('[Player] Asset not found for item:', item.assetId);
      this.consecutiveSkips++;
      this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);
      if (this.isPlaying) {
        this.playTimer = setTimeout(() => this.playNextItem(), 1000);
      }
      return;
    }

    // Handle composition assets — render as a temporary multi-zone layout
    if (asset.type === 'composition') {
      // Skip empty compositions
      const visibleZones = (asset.zones || []).filter(z => z.visible !== false);
      if (visibleZones.length === 0) {
        console.warn('[Player] Composition has no visible zones:', asset.id);
        this.consecutiveSkips++;
        this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);
        if (this.isPlaying) this.playTimer = setTimeout(() => this.playNextItem(), 1000);
        return;
      }

      const manifest = manifestLoader.getManifest();
      if (!manifest) {
        this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);
        if (this.isPlaying) this.playTimer = setTimeout(() => this.playNextItem(), 1000);
        return;
      }

      this.updateStatus({ status: 'playing', playlist: this.currentPlaylist.id });
      analytics.setCurrentContent(this.currentPlaylist.id, asset.id, this.currentScheduleRuleId);
      if (!this.isInBrightnessBlackout) {
        eventReporter.reportPlaybackStart(this.currentPlaylist.id, asset.id, this.currentScheduleRuleId);
      }

      let cleanup: (() => void) | null = null;
      try {
        // Convert composition zones to a ManifestLayout for the existing layout renderer
        const compositionLayout = {
          id: `comp-${asset.id}`,
          name: 'Composition',
          width: asset.width,
          height: asset.height,
          orientation: 'LANDSCAPE' as const,
          zones: visibleZones.map((z, i) => ({
            id: `comp-zone-${i}`,
            name: z.name,
            x: z.x,
            y: z.y,
            width: z.width,
            height: z.height,
            zIndex: z.zIndex || 0,
            contentType: z.contentType as 'ASSET' | 'PLAYLIST' | 'TEXT' | 'WIDGET' | 'DESIGN',
            content: z.content as import('../types').ZoneContent | undefined,
            assetId: z.assetId || undefined,
            playlistId: z.playlistId || undefined,
            designId: z.designId || undefined,
            fitMode: (z.fitMode || 'FILL') as 'FIT' | 'FILL',
            backgroundColor: z.backgroundColor || undefined,
            visible: true,
          })),
        };

        console.log('[Player] Rendering composition:', asset.id, 'zones:', compositionLayout.zones.length);
        cleanup = await layoutManager.renderTemporaryLayout(compositionLayout, manifest, 'player-container');
        this.lastPlaybackTime = Date.now();
        this.consecutiveSkips = 0;

        const durationMs = this.sanitizeDurationMs(item.duration, 'composition');
        await this.wait(durationMs);
      } catch (error) {
        console.error('[Player] Error playing composition:', error);
        this.lastPlaybackTime = Date.now();
      } finally {
        // Always clean up composition resources, even if wait() was interrupted
        if (cleanup) cleanup();
      }

      this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);
      if (this.isPlaying) {
        // Defer — see playNextItem reentrancy guard.
        this.playTimer = setTimeout(() => this.playNextItem(), 0);
      }
      return;
    }

    this.updateStatus({
      status: 'playing',
      playlist: this.currentPlaylist.id,
      item: {
        id: item.id,
        assetId: item.assetId || asset.id,
        assetType: asset.type.toUpperCase() as 'IMAGE' | 'VIDEO' | 'HTML' | 'WIDGET',
        url: asset.url,
        duration: item.duration,
      },
    });

    analytics.setCurrentContent(this.currentPlaylist.id, item.assetId || asset.id, this.currentScheduleRuleId);
    if (!this.isInBrightnessBlackout) {
      eventReporter.reportPlaybackStart(this.currentPlaylist.id, item.assetId || asset.id, this.currentScheduleRuleId);
    }

    try {
      console.log('[Player] Rendering asset:', {
        id: asset.id,
        type: asset.type,
        url: asset.url,
        fitMode: item.fitMode,
      });

      // Render the item using asset data with fit mode
      await renderer.renderAsset(asset, this.currentPlaylist.id, item.fitMode);
      console.log('[Player] Asset rendered successfully');

      // Successful playback — reset skip counter
      this.consecutiveSkips = 0;

      // Update watchdog - we're still alive
      this.lastPlaybackTime = Date.now();

      // Background preload next asset while current one plays
      this.preloadNextAsset();

      // For videos: use item duration and loop if video is shorter
      // For images: wait for item duration
      if (asset.type === 'video') {
        const hasItemDuration = Number.isFinite(item.duration) && (item.duration as number) > 0;
        const hasVideoDuration = Number.isFinite(asset.duration as number) && (asset.duration as number) > 0;
        const itemDurationMs = hasItemDuration ? (item.duration as number) * 1000 : 0;
        const videoDurationMs = hasVideoDuration ? (asset.duration as number) * 1000 : 0;

        if (hasItemDuration && hasVideoDuration && itemDurationMs > videoDurationMs) {
          // Video is shorter than item duration — enable looping and wait for item duration
          console.log(`[Player] Video (${videoDurationMs}ms) shorter than item duration (${itemDurationMs}ms), looping`);
          renderer.enableVideoLoop(true);
          await this.wait(itemDurationMs);
        } else if (hasItemDuration && hasVideoDuration && itemDurationMs <= videoDurationMs) {
          // Item duration is shorter or equal to video — play for item duration only
          console.log(`[Player] Playing video for item duration: ${itemDurationMs}ms`);
          await this.wait(itemDurationMs);
        } else if (hasItemDuration) {
          // Item duration known but video duration unknown — honor item duration anyway
          // (looping enabled in case the video is shorter; trims it if longer).
          console.log(`[Player] Playing video for item duration only: ${itemDurationMs}ms`);
          renderer.enableVideoLoop(true);
          await this.wait(itemDurationMs);
        } else if (hasVideoDuration) {
          // Item duration missing but video duration known — wait for video length.
          console.log(`[Player] Playing video for natural duration: ${videoDurationMs}ms`);
          await this.wait(videoDurationMs);
        } else {
          // No reliable duration info anywhere — play video once to completion,
          // but cap at 60s so a broken/paused video doesn't burn a slot for the
          // entire heat death of the universe and doesn't ALSO collapse to 0
          // (renderer.waitForCompletion historically resolved on video.paused,
          // which would advance instantly when autoplay was blocked).
          console.log('[Player] No duration info — using waitForCompletion with 60s cap');
          await Promise.race([renderer.waitForCompletion(), this.wait(60_000)]);
        }
      } else {
        // item.duration is in seconds from manifest. sanitizeDurationMs floors
        // missing/invalid values to a sane image dwell (10s) rather than
        // collapsing to 0 and flashing the asset for a single frame.
        const durationMs = this.sanitizeDurationMs(item.duration, 'image');
        console.log('[Player] Waiting for image duration:', durationMs, 'ms (item.duration=', item.duration, ')');
        await this.wait(durationMs);
      }

      // Move to next item
      this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);

      // Continue playing — defer so the outer playNextItem unwinds (clears
      // isAdvancingItem) before the next chain starts.
      if (this.isPlaying) {
        this.playTimer = setTimeout(() => this.playNextItem(), 0);
      }
    } catch (error) {
      console.error('[Player] Error playing item:', error);
      const errorMsg = error instanceof Error ? error.message : 'Playback error';
      eventReporter.reportPlaybackError(errorMsg, item.assetId || asset.id);

      // Update watchdog even on error
      this.lastPlaybackTime = Date.now();

      // Increment consecutive skip counter
      this.consecutiveSkips++;

      // Skip to next item
      this.setCurrentItemIndex(this.pickNextIndex(this.currentItemIndex, items.length), 'advance', items.length);

      if (this.isPlaying) {
        this.playTimer = setTimeout(() => this.playNextItem(), 1000);
      }
    }
  }

  /**
   * Ensure playback continues even if an unhandled error occurs
   */
  private safePlayNext(): void {
    try {
      this.playNextItem().catch((error) => {
        console.error('[Player] Unhandled error in playNextItem:', error);
        this.lastPlaybackTime = Date.now();
        if (this.isPlaying) {
          this.playTimer = setTimeout(() => this.safePlayNext(), 2000);
        }
      });
    } catch (error) {
      console.error('[Player] Sync error in safePlayNext:', error);
      this.lastPlaybackTime = Date.now();
      if (this.isPlaying) {
        this.playTimer = setTimeout(() => this.safePlayNext(), 2000);
      }
    }
  }

  /**
   * Preload the next asset in the playlist so it's ready when transition happens.
   * Runs in the background — errors are silently ignored.
   */
  private preloadNextAsset(): void {
    if (!this.currentPlaylist) return;

    const items = this.currentPlaylist.items;
    const assets = this.currentPlaylist.assets;
    if (items.length <= 1) return;

    const nextIndex = (this.currentItemIndex + 1) % items.length;
    const nextItem = items[nextIndex];
    const nextAsset = nextItem.assetId ? assets.get(nextItem.assetId) : undefined;

    if (!nextAsset) return;
    // Only image/video are handled by renderer.prepareAsset — compositions,
    // designs and layouts go through different code paths.
    if (nextAsset.type !== 'image' && nextAsset.type !== 'video') return;

    // Build the next element via the real render pipeline (blob URL from
    // IndexedDB, canplay wait, decode) while the current item is still on
    // screen. renderAsset will adopt the prebuilt element instantly, so the
    // swap-in is immediate — no freeze on the last frame of the previous
    // video. Best-effort; failures fall back to on-demand creation.
    renderer.prepareAsset(nextAsset, nextItem.fitMode || 'FIT').catch(() => null);
  }

  // Duration-bounded wait. Crucially, the timer is LOCAL to this call — it
  // does NOT share state with this.playTimer. The shared field is written
  // and cleared from many unrelated paths (stop, recoverFromFreeze, schedule
  // blackout, the prologue clear in playNextItem). Sharing the timer with
  // those paths used to cause `wait()` to either hang (when cleared) or be
  // overwritten before firing — producing the "sometimes goes directly
  // without waiting the full duration" symptom.
  //
  // We also enforce a floor of 100ms to guard against item.duration === 0
  // / NaN / undefined from a malformed manifest. The duration sanitization
  // upstream (sanitizeDurationMs) handles the typical case; this floor is a
  // last-line defense.
  private wait(ms: number): Promise<void> {
    const safeMs = Number.isFinite(ms) && ms > 100 ? ms : 100;
    return new Promise((resolve) => {
      setTimeout(resolve, safeMs);
    });
  }

  // Image items default to 10s; everything else floored to 1s if missing.
  // Called at every duration site (image, video, design, layout, composition)
  // so a manifest with `duration: 0` no longer collapses the loop.
  private sanitizeDurationMs(seconds: number | null | undefined, kind: 'image' | 'video' | 'design' | 'layout' | 'composition'): number {
    const s = Number.isFinite(seconds) && (seconds as number) > 0 ? (seconds as number) : 0;
    if (s > 0) return s * 1000;
    if (kind === 'image') return 10_000; // typical signage image dwell
    return 1_000; // sanity floor — never zero
  }

  private async reportDeviceProfile(): Promise<void> {
    try {
      // Get actual screen resolution (not scaled by devicePixelRatio for display)
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const rotation = window.screen.orientation?.angle || 0;
      const density = window.devicePixelRatio || 1;

      const userAgent = navigator.userAgent;

      // Detect OS and version
      let osName = 'Unknown OS';
      let osVersion = '';

      if (userAgent.includes('Mac OS X')) {
        osName = 'macOS';
        const match = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
        if (match) {
          osVersion = match[1].replace(/_/g, '.');
        }
      } else if (userAgent.includes('Windows NT')) {
        osName = 'Windows';
        const match = userAgent.match(/Windows NT (\d+\.\d+)/);
        if (match) {
          const ntVersion = match[1];
          // Map NT version to Windows version
          const winVersions: Record<string, string> = {
            '10.0': '10/11',
            '6.3': '8.1',
            '6.2': '8',
            '6.1': '7',
          };
          osVersion = winVersions[ntVersion] || ntVersion;
        }
      } else if (userAgent.includes('Linux')) {
        osName = 'Linux';
        if (userAgent.includes('Android')) {
          osName = 'Android';
          const match = userAgent.match(/Android (\d+\.?\d*)/);
          if (match) osVersion = match[1];
        }
      } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
        osName = 'iOS';
        const match = userAgent.match(/OS (\d+_\d+)/);
        if (match) osVersion = match[1].replace('_', '.');
      }

      // Detect hardware/platform
      let manufacturer = 'Unknown';
      let model = 'Web Player';

      // Check if running in Electron
      const isElectron = userAgent.includes('Electron') ||
                        (window as unknown as { process?: { type?: string } }).process?.type === 'renderer';

      if (isElectron) {
        model = 'Electron App';
        if (osName === 'macOS') {
          manufacturer = 'Apple';
          // Try to detect Mac model from platform
          if (navigator.platform === 'MacIntel' || userAgent.includes('Mac')) {
            model = 'Mac (Electron)';
          }
        } else if (osName === 'Windows') {
          manufacturer = 'PC';
          model = 'Windows PC (Electron)';
        } else if (osName === 'Linux') {
          manufacturer = 'PC';
          model = 'Linux PC (Electron)';
        }
      } else {
        // Browser detection
        if (userAgent.includes('Edg/')) {
          manufacturer = 'Microsoft';
          model = 'Edge Browser';
        } else if (userAgent.includes('Chrome/') && !userAgent.includes('Chromium')) {
          manufacturer = 'Google';
          model = 'Chrome Browser';
        } else if (userAgent.includes('Firefox/')) {
          manufacturer = 'Mozilla';
          model = 'Firefox Browser';
        } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
          manufacturer = 'Apple';
          model = 'Safari Browser';
        }

        // Add platform info for browsers
        if (osName === 'macOS') {
          manufacturer = 'Apple';
        }
      }

      // Detect video capabilities
      const video = document.createElement('video');
      const supportsH265 = video.canPlayType('video/mp4; codecs="hvc1"') !== '' ||
                          video.canPlayType('video/mp4; codecs="hev1"') !== '';
      const supports4k = (screenWidth * density) >= 3840 || (screenHeight * density) >= 2160;

      const fullOsString = osVersion ? `${osName} ${osVersion}` : osName;

      await api.reportProfile({
        screenWidth: Math.round(screenWidth),
        screenHeight: Math.round(screenHeight),
        rotation,
        density: Math.round(density * 100) / 100,
        model,
        manufacturer,
        androidVersion: fullOsString, // Repurpose this field for OS info
        capabilities: {
          supportsH265,
          supports4k,
          supportsHdr: false, // Can't reliably detect HDR in browsers
          maxBitrate: 50000000, // 50 Mbps - reasonable for web
          hasAudio: true,
        },
      });

      console.log('Device profile reported:', { model, manufacturer, os: fullOsString, screen: `${screenWidth}x${screenHeight}` });
    } catch (error) {
      console.warn('Failed to report device profile:', error);
      // Don't throw - profile reporting is not critical
    }
  }

  /**
   * Apply device orientation via CSS transform on the player container.
   * The TV always outputs landscape; we rotate the content container via CSS.
   * For 90/270, content renders in a virtual portrait viewport (e.g. 1080x1920).
   */
  /**
   * Apply manifest.screenBrightness — pure CSS, no native API needed.
   *  - 0    → blackout overlay opaque (display fully black)
   *  - 1–99 → CSS filter on body dims everything proportionally
   *  - 100 / null → no override (default brightness, overlay hidden)
   *
   * Tizen/webOS/Windows-Electron all bundle this same player core, so the
   * "Ekranı Kapat" remote command works on every player except Android
   * (which uses a real WindowManager brightness API).
   */
  private applyScreenBrightness(brightness: number | null): void {
    console.log('[Player] applyScreenBrightness:', brightness);
    // Tracked separately from the overlay-toggle logic below so the
    // value flips even if (for some reason) the DOM mutation fails.
    this.isInBrightnessBlackout = brightness !== null && brightness <= 0;
    const body = document.body;
    if (brightness === null || brightness >= 100) {
      const overlay = document.getElementById('blackout-overlay');
      if (overlay) {
        overlay.style.opacity = '0';
        overlay.style.display = 'none';
      }
      body.style.filter = '';
      return;
    }
    if (brightness <= 0) {
      // Blackout: ensure the overlay exists. The schedule-driven
      // `showBlackoutScreen` creates it lazily, but a manifest-driven
      // `screenBrightness: 0` (the dashboard "Enable Blackout" button)
      // can hit this path long before any schedule has ever blacked
      // the screen out — so without this ensure-and-create step, the
      // call was a silent no-op and the screen stayed bright while
      // the dashboard insisted it was off.
      const overlay = this.ensureBlackoutOverlay();
      overlay.style.display = 'block';
      overlay.style.opacity = '1';
      body.style.filter = '';
      return;
    }
    // 1–99: dim via CSS filter; the overlay (if any) is hidden.
    const overlay = document.getElementById('blackout-overlay');
    if (overlay) {
      overlay.style.opacity = '0';
      overlay.style.display = 'none';
    }
    body.style.filter = `brightness(${(brightness / 100).toFixed(2)})`;
  }

  /**
   * Create the blackout overlay if it doesn't exist. Shared by both the
   * schedule path (`showBlackoutScreen`) and the manifest brightness
   * path (`applyScreenBrightness`) so a brightness-0 command works even
   * when no schedule has ever fired.
   */
  private ensureBlackoutOverlay(): HTMLDivElement {
    let overlay = document.getElementById('blackout-overlay') as HTMLDivElement | null;
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.id = 'blackout-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: black;
      z-index: 9998;
      transition: opacity 0.2s ease;
      opacity: 0;
      display: none;
    `;
    document.body.appendChild(overlay);
    return overlay;
  }

  private applyDeviceOrientation(manifest: DeviceManifest, force = false): void {
    const orientation = manifest.deviceOrientation ?? 0;

    // Skip if unchanged AND we've already attached the resize listener.
    // `force=true` bypasses this — used by the per-chain orientation
    // reassertion below so a downstream cssText wipe (e.g., a layout
    // mode swap or a renderer mode reinit) can't permanently strip the
    // rotation transform.
    if (!force && orientation === this.currentOrientation && this.orientationResizeBound) return;
    this.currentOrientation = orientation;

    // Re-apply transforms on viewport resize so the rotated container always
    // fills the current screen (TVs rarely resize, but the webOS simulator
    // and dev browsers do, and getting the math wrong leaves the content
    // clipped in a top-left rectangle — exactly the symptom the previous
    // implementation produced because transform-origin was set to the
    // SCREEN center instead of (0,0), without compensating translation).
    if (!this.orientationResizeBound) {
      this.orientationResizeBound = true;
      window.addEventListener('resize', () => {
        // Force re-apply using the cached manifest.
        const cachedManifest = manifestLoader.getManifest();
        if (cachedManifest) {
          // Bypass the unchanged-orientation early-return:
          const o = this.currentOrientation;
          this.currentOrientation = -1;
          this.applyDeviceOrientation({ ...cachedManifest, deviceOrientation: o } as DeviceManifest);
        }
      });
    }

    const container = document.getElementById('player-container');
    if (!container) return;

    // Always reset first — then re-apply if non-zero.
    container.style.position = '';
    container.style.left = '';
    container.style.top = '';
    container.style.width = '';
    container.style.height = '';
    container.style.transformOrigin = '';
    container.style.transform = '';

    if (orientation === 0) {
      console.log('[Player] Applied device orientation: 0° (landscape default)');
      return;
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Skip CSS rotation if the viewport already matches the requested
    // orientation. The webOS TV Simulator's "Portrait" toggle resizes the
    // canvas to 1080×1920 — in that case the simulated TV is already in
    // a portrait viewport and rotating content 90° again would put it
    // sideways. Same for any host that does its own orientation handling.
    //
    // Rule: portrait orientation (90°/270°) is only achieved via CSS
    // rotation when the viewport itself is wider than tall. If vh > vw,
    // the viewport is already portrait and we leave the content alone.
    const viewportIsPortrait = vh > vw;
    const requestedIsPortrait = orientation === 90 || orientation === 270;
    if (requestedIsPortrait && viewportIsPortrait) {
      console.log(`[Player] Orientation ${orientation}° requested; viewport ${vw}x${vh} is already portrait — skipping CSS rotation.`);
      return;
    }

    if (orientation === 90) {
      // Portrait-clockwise. Element sized (vh × vw) — i.e. (1080 × 1920)
      // in a 1920×1080 landscape viewport — placed at (0,0). After
      // rotate(90deg) around its own (0,0) the post-rotation extent is
      // x:[-vw, 0], y:[0, vh]. So we translate right by vw (NOT vh, which
      // was the previous bug — undershooting by 840px and clipping the
      // element to the top-left 1080×1080 square).
      // CSS transforms compose right-to-left: rotate first, then translate.
      container.style.position = 'absolute';
      container.style.left = '0';
      container.style.top = '0';
      container.style.width = `${vh}px`;
      container.style.height = `${vw}px`;
      container.style.transformOrigin = '0 0';
      container.style.transform = `translate(${vw}px, 0) rotate(90deg)`;
    } else if (orientation === 270) {
      // Portrait-counter-clockwise. rotate(-90deg) around (0,0) sends the
      // post-rotation element to x:[0, vw], y:[-vh, 0]. Translate down by
      // vh (NOT vw) to bring it back into the viewport.
      container.style.position = 'absolute';
      container.style.left = '0';
      container.style.top = '0';
      container.style.width = `${vh}px`;
      container.style.height = `${vw}px`;
      container.style.transformOrigin = '0 0';
      container.style.transform = `translate(0, ${vh}px) rotate(-90deg)`;
    } else if (orientation === 180) {
      // 180° — same dimensions, flipped. Center-pivot is safe here because
      // the rotated bounding box equals the original.
      container.style.transformOrigin = 'center center';
      container.style.transform = 'rotate(180deg)';
    }

    const isPortrait = orientation === 90 || orientation === 270;
    console.log(`[Player] Applied device orientation: ${orientation}°${isPortrait ? ' (portrait)' : ''} viewport=${vw}x${vh} container=${container.style.width}x${container.style.height}`);

    // Diagnostic dump — verifies the inline transform actually stuck after
    // applying it. If the user-visible content doesn't appear rotated even
    // though the log above says we applied 90°, this dump will show whether
    // the computed transform is the matrix we expect, OR something else is
    // overriding it. Read on first apply only.
    setTimeout(() => {
      const cs = window.getComputedStyle(container);
      const rect = container.getBoundingClientRect();
      const child = container.firstElementChild as HTMLElement | null;
      const childRect = child?.getBoundingClientRect();
      const childCs = child ? window.getComputedStyle(child) : null;
      console.log('[Player:rot-debug] container computed:', {
        position: cs.position,
        width: cs.width,
        height: cs.height,
        transform: cs.transform,
        transformOrigin: cs.transformOrigin,
        rect: { x: rect.x, y: rect.y, w: rect.width, h: rect.height },
        childCount: container.children.length,
      });
      if (child && childRect && childCs) {
        console.log('[Player:rot-debug] first-child computed:', {
          tag: child.tagName,
          id: child.id,
          position: childCs.position,
          width: childCs.width,
          height: childCs.height,
          transform: childCs.transform,
          rect: { x: childRect.x, y: childRect.y, w: childRect.width, h: childRect.height },
        });
      } else {
        console.log('[Player:rot-debug] container has no children yet');
      }
    }, 50);
  }

  // Sign a message with HMAC-SHA256 for WebSocket authentication
  private async signMessage(data: string, base64Secret: string): Promise<string> {
    const encoder = new TextEncoder();
    const binaryString = atob(base64Secret);
    const keyBytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      keyBytes[i] = binaryString.charCodeAt(i);
    }

    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(data));
    return btoa(String.fromCharCode(...new Uint8Array(signature)));
  }

  private async onManifestUpdate(manifest: DeviceManifest): Promise<void> {
    console.log('Manifest updated:', manifest.version);

    // Update device orientation if changed
    this.applyDeviceOrientation(manifest);

    // Apply screen brightness override (0 = blackout, 1-99 = dim, 100/null = default)
    this.applyScreenBrightness(manifest.screenBrightness ?? null);

    scheduler.setManifest(manifest);
    analytics.setManifestVersion(manifest.version);
    eventReporter.reportManifestSync(manifest.version);

    // Check if layout mode changed
    const newUseMultiZone = !!manifest.layout;

    if (newUseMultiZone !== this.useMultiZone) {
      // Mode changed - need to reinitialize
      console.log(`Layout mode changed: ${this.useMultiZone ? 'multi' : 'single'} -> ${newUseMultiZone ? 'multi' : 'single'}`);
      await this.stop();
      this.useMultiZone = newUseMultiZone;

      if (this.useMultiZone) {
        layoutManager.initialize('player-container');
        await layoutManager.setManifest(manifest);
        layoutManager.setPlayEventCallback((event) => {
          if (this.isInBrightnessBlackout) return;
          analytics.recordPlayEvent(event);
        });
        await layoutManager.setLayout(manifest.layout || null);
      } else {
        renderer.initialize('player-container');
        renderer.setPlayEventCallback((event) => {
          if (this.isInBrightnessBlackout) return;
          analytics.recordPlayEvent(event);
        });
      }

      if (this.isPlaying) {
        await this.start();
      }
    } else if (this.useMultiZone) {
      // Update layout manager with new manifest
      await layoutManager.setManifest(manifest);

      // Check if layout changed
      if (manifest.layout) {
        await layoutManager.setLayout(manifest.layout);
        await layoutManager.startPlayback();
      }
    } else {
      // Legacy mode - just check schedule
      this.checkSchedule();
    }

    // Update overlays
    if (manifest.overlays && manifest.overlays.length > 0) {
      console.log('[Player] Updating overlays:', manifest.overlays.length);
      overlayRenderer.setOverlays(manifest.overlays as OverlayConfig[]);
    } else {
      // Clear overlays if none in manifest
      overlayRenderer.clear();
    }
  }

  private async connectWebSocket(): Promise<void> {
    if (!this.registration) return;

    try {
      const wsUrl = `${config.wsUrl}?deviceId=${this.registration.deviceId}`;
      this.wsConnection = new WebSocket(wsUrl);

      this.wsConnection.onopen = async () => {
        console.log('WebSocket connected, authenticating...');
        // NOTE: do NOT reset backoff here — auth failure causes onclose to
        // fire and onclose-only path schedules the next reconnect. Resetting
        // backoff before HELLO acks would have meant every auth-rejected
        // cycle came back in ~1s with "attempt 1" labelling, which produced
        // the reconnect storm we just hunted down. Backoff resets after a
        // first successful PONG instead (see handleWebSocketMessage).

        // Send HELLO message with authentication.
        // Signed payload MUST match the format the ws-gateway verifier
        // expects (see apps/ws-gateway/src/auth/device-auth.ts:69) —
        // METHOD|path|timestamp|bodyHash where bodyHash is empty for HELLO.
        const timestamp = Math.floor(Date.now() / 1000);
        const signature = await this.signMessage(
          `WS|/hello|${timestamp}|`,
          this.registration!.deviceSecret
        );

        this.wsConnection?.send(JSON.stringify({
          type: 'HELLO',
          deviceId: this.registration!.deviceId,
          timestamp,
          signature,
          appVersion: '1.0.0',
        }));

        // Start PING/PONG keep-alive
        this.startPingLoop();
      };

      this.wsConnection.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleWebSocketMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.wsConnection.onclose = () => {
        console.log('WebSocket disconnected');
        this.stopPingLoop();
        this.scheduleReconnect();
      };

      this.wsConnection.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    // Exponential backoff with jitter
    const jitter = Math.random() * 0.3 * this.wsReconnectDelay;
    const delay = Math.min(this.wsReconnectDelay + jitter, this.WS_MAX_RECONNECT);

    this.wsReconnectAttempts++;
    console.log(`Reconnecting WebSocket in ${Math.round(delay)}ms (attempt ${this.wsReconnectAttempts})...`);

    this.reconnectTimeout = setTimeout(() => {
      this.connectWebSocket();
    }, delay);

    // Increase delay for next attempt
    this.wsReconnectDelay = Math.min(
      this.wsReconnectDelay * this.WS_BACKOFF_MULTIPLIER,
      this.WS_MAX_RECONNECT
    );
  }

  private resetReconnectBackoff(): void {
    this.wsReconnectDelay = this.WS_INITIAL_RECONNECT;
    this.wsReconnectAttempts = 0;
  }

  private startPingLoop(): void {
    this.stopPingLoop();

    this.pingInterval = setInterval(() => {
      if (this.wsConnection?.readyState === WebSocket.OPEN) {
        this.wsConnection.send(JSON.stringify({ type: 'PING' }));

        // Set timeout for PONG response
        this.pongTimeout = setTimeout(() => {
          console.warn('WebSocket PONG timeout, forcing reconnect');
          this.wsConnection?.close();
        }, this.WS_PONG_TIMEOUT);
      }
    }, this.WS_PING_INTERVAL);
  }

  private stopPingLoop(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  // 3C — force the SW to check for an updated bundle before reloading,
  // so the reload definitely picks up the latest JS/CSS/HTML. The default
  // PWA registration only checks for updates on its own cadence (~24h),
  // which means an immediate reload would just re-serve the cached old
  // bundle. update() returns when the check completes (either no change
  // or new worker installed in `waiting` state); the subsequent reload
  // triggers skipWaiting via vite-plugin-pwa's autoUpdate registration.
  private async checkServiceWorkerThenReload(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          console.log('[Player] Forcing SW registration.update() before reload');
          await registration.update();
        }
      }
    } catch (err) {
      console.warn('[Player] SW update check failed (continuing with reload):', err);
    }
    setTimeout(() => window.location.reload(), 500);
  }

  private handleWebSocketMessage(message: { type: string; payload?: unknown }): void {
    // ANY server message after HELLO confirms auth went through (the gateway
    // closes the socket synchronously on bad signature instead of sending
    // anything). Reset the reconnect backoff so a much-later disconnect
    // doesn't start the next cycle with an accumulated multi-second delay.
    if (this.wsReconnectAttempts > 0 || this.wsReconnectDelay !== this.WS_INITIAL_RECONNECT) {
      this.resetReconnectBackoff();
    }
    switch (message.type) {
      case 'MANIFEST_INVALIDATED':
        console.log('Manifest invalidated, refreshing...');
        manifestLoader.refresh().catch((error) => {
          console.error('Manifest refresh failed:', error);
        });
        break;

      case 'COMMAND':
        this.handleCommand(message.payload as { command: string; params?: Record<string, unknown> });
        break;

      // Handle direct command types from API (COMMAND_REFRESH, COMMAND_REBOOT, etc.)
      case 'COMMAND_REFRESH':
        console.log('Refresh command received');
        manifestLoader.refresh().catch((error) => {
          console.error('Manifest refresh failed:', error);
        });
        break;

      case 'COMMAND_REBOOT':
        console.log('Reboot command received');
        window.location.reload();
        break;

      case 'COMMAND_APP_RESTART':
        console.log('App restart command received');
        window.location.reload();
        break;

      case 'COMMAND_UPDATE_CHECK':
        console.log('Update check command received');
        if (window.electronAPI?.checkForUpdates) {
          // Electron (Windows-player): real auto-updater check
          window.electronAPI.checkForUpdates();
        } else {
          // Browser / Tizen / webOS: ask the service worker to check the
          // registered scripts for an update FIRST, then reload. Without
          // the explicit registration.update() call, the SW only checks
          // on its own internal cadence (~24h for app updates) and the
          // reload would just serve the cached old bundle. After calling
          // update(), the new worker enters `waiting` state; the reload
          // activates it via skipWaiting (registerType: 'autoUpdate' in
          // vite-plugin-pwa flips this for us).
          void this.checkServiceWorkerThenReload();
        }
        break;

      case 'COMMAND_INSTALL_UPDATE':
        // Operator-triggered: apply the update that's already been
        // downloaded in the background by electron-updater. Different
        // from UPDATE_CHECK above — that just polls latest.yml; this
        // actually exits the process and lets the OS swap binaries.
        //
        // Safe-by-default: on platforms where there's no electron host
        // (web / Tizen / webOS), this is a no-op — those platforms don't
        // have a downloaded binary to swap in. The dashboard UI should
        // gate the button on the device's appType to avoid misleading
        // operators, but we don't crash if it slips through.
        console.log('Install-update command received');
        if (window.electronAPI?.installUpdate) {
          window.electronAPI.installUpdate();
        } else {
          console.log('[Player] No electron host — INSTALL_UPDATE ignored');
        }
        break;

      case 'COMMAND_SCREENSHOT':
        console.log('Screenshot command received via WS');
        // Dispatch the legacy event for any external listener (e.g. Electron
        // host on Windows-player can do a native screen capture). If nothing
        // catches that, fall back to our in-browser DOM-composite capture
        // so the dashboard "Capture screenshot" button works on web + webOS
        // too (parity with Android).
        window.dispatchEvent(new CustomEvent('screenshot-requested'));
        if (!window.electronAPI?.captureScreenshot) {
          void captureAndUploadScreenshot();
        }
        break;

      case 'COMMAND_CLEAR_CACHE':
        console.log('Clear-cache command received via WS');
        // Same flow as the legacy remote-command event handler in initialize() —
        // wipe IndexedDB + localStorage, then reload so the player re-fetches
        // a fresh manifest and assets on startup.
        clearAllData()
          .catch((err) => console.error('[Player] clearAllData failed:', err))
          .finally(() => window.location.reload());
        break;

      case 'COMMAND_CAMERA_DEBUG': {
        const enabled = (message.payload as { enabled?: boolean })?.enabled ?? false;
        console.log('Camera debug toggle:', enabled);
        window.dispatchEvent(new CustomEvent('camera-debug-toggle', { detail: { enabled } }));
        break;
      }

      case 'PONG':
        // Clear pong timeout — connection is alive
        if (this.pongTimeout) {
          clearTimeout(this.pongTimeout);
          this.pongTimeout = null;
        }
        break;

      default:
        console.log('Unknown WebSocket message:', message.type);
    }
  }

  private handleCommand(payload: { command: string; params?: Record<string, unknown> }): void {
    switch (payload.command) {
      case 'REFRESH':
        console.log('Refresh command received');
        manifestLoader.refresh().catch((error) => {
          console.error('Manifest refresh failed:', error);
        });
        break;

      case 'RESTART_APP':
        console.log('Restart command received');
        window.location.reload();
        break;

      case 'SCREENSHOT':
        console.log('Screenshot command received');
        window.dispatchEvent(new CustomEvent('screenshot-requested'));
        break;

      case 'UPDATE_CHECK':
        console.log('Update check command received');
        window.electronAPI?.checkForUpdates();
        break;

      default:
        console.log('Unknown command:', payload.command);
    }
  }

  private updateStatus(update: PlayerStatusUpdate): void {
    if (this.statusCallback) {
      this.statusCallback(update);
    }
  }

  /**
   * Show error overlay when skip cooldown is active
   */
  private showSkipCooldownOverlay(): void {
    let overlay = document.getElementById('skip-cooldown-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'skip-cooldown-overlay';
      overlay.style.cssText = `
        position: fixed; top: 0; left: 0; width: 100%; height: 100%;
        background: rgba(0,0,0,0.85); z-index: 9997;
        display: flex; align-items: center; justify-content: center;
      `;
      overlay.innerHTML = `
        <div style="color: #ff4444; text-align: center; font-family: sans-serif;">
          <div style="font-size: 24px; margin-bottom: 12px;">Content Playback Error</div>
          <div style="font-size: 14px; color: #aaa;">All assets failed to load. Retrying in 30 seconds...</div>
        </div>
      `;
      document.body.appendChild(overlay);
    }
    overlay.style.display = 'flex';
  }

  private hideSkipCooldownOverlay(): void {
    const overlay = document.getElementById('skip-cooldown-overlay');
    if (overlay) overlay.style.display = 'none';
  }

  destroy(): void {
    this.stop();
    analytics.stop();
    eventReporter.stop();
    manifestLoader.destroy();

    // Stop power schedule monitoring
    scheduler.stopPowerScheduleMonitor();

    // Stop schedule monitoring
    scheduler.stopScheduleMonitor();

    this.stopPingLoop();

    if (this.wsConnection) {
      this.wsConnection.close();
      this.wsConnection = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Remove event listeners
    if (this.remoteCommandHandler) {
      window.removeEventListener('remote-command', this.remoteCommandHandler);
      this.remoteCommandHandler = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
      this.offlineHandler = null;
    }

    if (this.useMultiZone) {
      layoutManager.destroy();
    }

    // Clean up video wall and sync managers
    if (this.useVideoWall) {
      videoWallManager.destroy();
    }
    syncManager.destroy();

    // Clean up overlay renderer
    overlayRenderer.destroy();

    // Clean up single-zone renderer
    renderer.destroy();

    // Clean up fallback manager
    fallbackManager.destroy();

    // Remove power-off overlay if present
    const powerOverlay = document.getElementById('power-off-overlay');
    if (powerOverlay) {
      powerOverlay.remove();
    }

    // Remove blackout overlay if present
    const blackoutOverlay = document.getElementById('blackout-overlay');
    if (blackoutOverlay) {
      blackoutOverlay.remove();
    }

    // Remove skip cooldown overlay if present
    const skipOverlay = document.getElementById('skip-cooldown-overlay');
    if (skipOverlay) {
      skipOverlay.remove();
    }
  }
}

export const player = new Player();
